<template>
    <service-card
        :service-tips="serviceTips"
        :service="service"
        icon="mdi-lightning-bolt-outline"
        :is-first="isFirst"
        :order="order"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service="service"
                :service-image="mainImage"
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :is-package="isPackage"
                :tariff-name="service.serviceDetails[0].tariffName"
                :service-label="activityServiceName"
                :order="order"
                :order-end-date="orderEndDate"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
            >
                <v-skeleton-loader v-if="isInfoLoading" type="paragraph" />
                <template v-if="info && info.location" v-slot:additionalInfo>
                    <div class="order-info-base-info">
                        <span class="order-info-base-info__icon">
                            <v-icon start size="x-small">mdi-map-marker-outline</v-icon>
                            <span class="text-body-2">{{ $t('location') }}</span>
                        </span>
                        <span class="order-info-base-info__text d-flex align-center flex-wrap" style="gap: 0 6px">
                            {{ info.location }}
                            <span
                                v-if="departures.length && departures[0].latitude && departures[0].longitude"
                                class="text-body-2 text-link text-decoration-underline text-lowercase"
                                @click="mapModal = true"
                            >
                                {{ $t('show_on_map') }}
                            </span>
                        </span>
                        <VenueLabel
                            v-if="info?.venue"
                            :venue-name="info.venue.name"
                            :address="info.venue.address"
                            :hide-show-on-map-button="!info.venue.latitude || !info.venue.longitude"
                            :is-show-icon="false"
                            @click="venueMapModal = true"
                        />
                    </div>
                </template>
                <div v-if="transport" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-bus</v-icon>
                        <span class="text-body-2">{{ $t('transport') }}</span>
                    </span>
                    <span class="order-info-base-info__text"> {{ transport }} </span>
                </div>
                <div v-if="pickupLocation" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-map-marker-outline</v-icon>
                        <span class="text-body-2">{{ $t('pick_up_point') }}</span>
                    </span>
                    <span class="order-info-base-info__text"> {{ pickupLocation }} </span>
                </div>
                <activity-duration
                    :is-show-label="true"
                    :time="service.serviceDetails[0].durationTime"
                    :days="service.serviceDetails[0].durationDays"
                />
                <div v-if="info.themes && info.themes.length" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-star-circle</v-icon>
                        <span class="text-body-2">{{ $t('filterTitles.themes') }}</span>
                    </span>
                    <span class="order-info-base-info__text"> {{ info.themes.join(', ') }} </span>
                </div>
                <div v-if="activityCategory" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-star-circle</v-icon>
                        <span class="text-body-2">{{ $t('category') }}</span>
                    </span>
                    <span class="order-info-base-info__text"> {{ activityCategory }} </span>
                </div>
                <transfer-driver-languages
                    v-if="service.serviceDetails[0].language"
                    :is-show-label="true"
                    :lang-codes="[service.serviceDetails[0].language]"
                />
                <div v-if="service.status === 'Quote' && !isReadOnly" class="enter-details d-flex align-center">
                    <v-icon start size="small" color="primary" @click="fillActivityBookingDataModal = true">
                        mdi-pencil
                    </v-icon>
                    <span class="text-caption cursor-pointer text-primary" @click="fillActivityBookingDataModal = true">
                        {{ $t('enter_details') }}
                    </span>
                    <fill-activity-booking-data-modal
                        v-model="fillActivityBookingDataModal"
                        :service="service"
                        :order="order"
                        :info="info"
                    />
                </div>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <service-info-dialog
                ref="serviceInfoDialog"
                v-model="modal"
                :service="service"
                :service-type="
                    service.serviceDetails[0].type && service.serviceDetails[0].type !== 'UNKNOWN'
                        ? $t(`filters_activityType.${service.serviceDetails[0].type}`)
                        : null
                "
                :info="info"
                enable-custom-product-info
                :image="mainImage"
                :is-info-loading="isInfoLoading"
                @close-modal="closeModal"
            >
                <template v-slot:serviceInfo>
                    <div v-if="language">
                        <strong>{{ $t('language') }}: {{ language }}</strong>
                    </div>
                    <div v-if="info.shortDescription" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('description_type.short') }}
                        </span>
                        <span v-html="info.shortDescription" />
                    </div>
                    <div v-if="info.fullDescription" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('description_type.general') }}
                        </span>
                        <span v-html="info.fullDescription" />
                    </div>
                    <div v-if="info.pleaseNote" class="mt-2">
                        <span class="d-block text-subtitle-1 font-weight-medium">
                            {{ $t('important_information') }}
                        </span>
                        <span v-html="info.pleaseNote" />
                    </div>
                    <div
                        v-if="info && info.descriptions && info.descriptions.length"
                        class="activity-page__descr d-flex flex-column"
                    >
                        <strong class="activity-page__descr-title">{{ $t('description') }} </strong>
                        <div
                            v-for="(description, index) in info.descriptions"
                            :key="index"
                            class="hotel-info-descriptions__item"
                        >
                            <h4 class="hotel-info-descriptions__item-title">
                                {{ descriptionTypeLabel(description) }}
                            </h4>
                            <div class="activity-page__descr-item mb-4" v-html="description.description" />
                        </div>
                    </div>
                    <template v-if="info && info.comments && info.comments.length">
                        <div class="font-weight-bold pt-4 pb-2">
                            {{ $t('comments') }}
                        </div>
                        <div v-for="(comm, index) in info.comments" :key="index" class="d-flex flex-column">
                            <span class="font-weight-medium">
                                {{ comm.title }}
                            </span>
                            <span>
                                {{ comm.description }}
                            </span>
                        </div>
                    </template>
                </template>
            </service-info-dialog>
            <client-only>
                <ModalWrapper
                    v-if="departures.length && departures[0].latitude && departures[0].longitude"
                    v-model="mapModal"
                    :max-width="500"
                >
                    <map-location
                        v-if="mapModal"
                        :latitude="departures[0].latitude"
                        :longitude="departures[0].longitude"
                        :height="`500px`"
                    />
                </ModalWrapper>
            </client-only>
            <client-only>
                <v-dialog
                    v-if="info && info.venue?.latitude && info.venue?.longitude"
                    v-model="venueMapModal"
                    :max-width="500"
                >
                    <map-location
                        v-if="venueMapModal"
                        :latitude="info.venue?.latitude"
                        :longitude="info.venue?.longitude"
                        :height="`500px`"
                    />
                </v-dialog>
            </client-only>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Inject} from 'vue-facing-decorator'
    import ServiceInfoDialog from '~/components/account/orderServices/ServiceInfoDialog.vue'
    import ServiceCard from '~src/components/account/orderServices/serviceCard.src'
    import ActivityDuration from '@/components/activities/search/offers/ActivityDuration.vue'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import FillActivityBookingDataModal from '@/components/account/modals/newOrderRequest/FillActivityBookingDataModal.vue'
    import MapLocation from '~src/components/info/mapLocation.src'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'
    import TransferDriverLanguages from '~/components/snippets/TransferDriverLanguages.vue'
    import VenueLabel from '~/components/activities/snippets/VenueLabel.vue'

    @Component({
        components: {
            VenueLabel,
            TransferDriverLanguages,
            FillActivityBookingDataModal,
            ActivityDuration,
            ServiceCard,
            OrderInfoBase,
            ServiceInfoDialog,
            MapLocation,
        },
    })
    export class ActivityService extends ServiceBase {
        @Prop({required: true}) service
        @Prop({default: false}) isPackage
        @Prop() itineraryService
        @Prop() isFirst
        @Inject({default: false}) isReadOnly

        activityInfo = null
        mapModal = false
        modal = false
        venueMapModal = false
        fillActivityBookingDataModal = false

        async loadProductInfo() {
            const processId = this.service.processId.replace(/^.{6}/, '')
            this.isInfoLoading = true

            try {
                this.activityInfo = await this.$api.activityInfo.get({processId})
            } catch (e) {
                /* empty */
            }

            this.activityInfo.departures?.forEach(e => {
                if (e.latitude && e.longitude) {
                    this.addMarker(e.latitude, e.longitude)
                }
            })

            this.isInfoLoading = false
            this.infoLoadingFinished = true
        }

        @Emit()
        addMarker(latitude, longitude) {
            return {lat: latitude, lng: longitude, type: 'activity'}
        }

        closeModal() {
            this.modal = false
        }

        descriptionTypeLabel(description) {
            if (this.$te(`description_type.${description?.type.toLowerCase()}`)) {
                return this.$t(`description_type.${description?.type.toLowerCase()}`)
            }

            return description?.type
        }

        get serviceCheckIn() {
            const time = this.service.serviceDetails[0].pickupTime
            const date = this.$filters.dateFormat(this.service.startDateTime)
            return time
                ? `<span>${date}</span> <small>${this.$t('time_from')} ${time}</small>`
                : `<span >${date}</span>`
        }

        get serviceCheckOut() {
            if (this.service.productVariantId === +this.$conf.public.productVariantIds?.multiDayTours) {
                const date = this.$filters.dateFormat(this.service.endDateTime)
                return `<span>${date}</span>`
            }

            return null
        }

        get mainImage() {
            if (this.activityInfo) {
                if (this.activityInfo.images && this.activityInfo.images.length) {
                    let image = this.activityInfo.images.find(el => el.mainImage)
                    if (!image) {
                        image = this.activityInfo.images[0]
                    }
                    return image.url
                }
                return null
            }
            return null
        }

        get info() {
            return {...this.activityInfo, ...{mainImage: this.mainImage}}
        }

        get departures() {
            return this.info.departures || []
        }

        get activityType() {
            return this.$t(`activities.type.${this.service.serviceDetails[0].type.toLowerCase()}`)
        }

        get activityCategory() {
            if (this.service.serviceDetails[0].activityCategories.length) {
                return this.service.serviceDetails[0].activityCategories[0].name
            }

            return null
        }

        get location() {
            if (this.service.serviceDetails[0].location) {
                return this.service.serviceDetails[0].location
            }

            return null
        }

        get activityServiceName() {
            if (this.service.serviceDetails[0].activityServiceName) {
                return this.service.serviceDetails[0].activityServiceName
            }

            return null
        }

        get transport() {
            if (this.service.serviceDetails[0].transport) {
                return this.service.serviceDetails[0].transport
            }

            return null
        }

        get pickupLocation() {
            if (this.service.serviceDetails[0].pickupLocation) {
                return this.service.serviceDetails[0].pickupLocation.name
            }

            return null
        }

        get language() {
            return this.service.serviceDetails[0].language
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default ActivityService
</script>

<style lang="scss" scoped>
    .thumb {
        max-width: 350px;
    }

    .col-title {
        white-space: nowrap;
        max-width: fit-content;
    }

    .payment-status {
        width: 100%;
        text-align: center;
        display: flex;
        justify-content: center;
        min-width: fit-content;
        max-width: fit-content;
    }

    .activity-type {
        text-transform: uppercase;
    }

    :deep(.v-dialog) {
        background: #fff;
        padding: 20px;
    }

    .modal-close-btn-block {
        position: relative;
        top: 12px;
        right: 12px;
    }
</style>
